import { useSelector } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import { signalTypeMap } from "app/pages/sales-intelligence/domain/signals/types/signal";
import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { selectIntentSignalSelectedTopics } from "app/pages/sales-intelligence/sub-modules/intent/store/signal/selectors/selectors";
import type { Topic } from "app/pages/sales-intelligence/sub-modules/intent/types/common";
import { IntentDataBanner } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/IntentDataBanner/IntentDataBanner";
import { IntentEmptyTopicsBanner } from "app/pages/sales-intelligence/sub-modules/signals/components/shared/IntentEmptyTopicsBanner";
import SignalsEmptyState from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalsEmptyState/SignalsEmptyState";
import SignalsListItems from "app/pages/sales-intelligence/sub-modules/signals/components/views/SignalsManagementView/SignalsListItems";
import {
    StyledIntentEmptyTopicStateWrapper,
    StyledScrollableSignalsList,
    StyledSignalsSection,
    StyledSignalsWrapper,
} from "app/pages/sales-intelligence/sub-modules/signals/components/views/SignalsManagementView/styles";
import type SignalFilters from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalFilters";
import SignalSource from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalSource";
import type UISignal from "app/pages/sales-intelligence/sub-modules/signals/models/view/UISignal";
import { useAccountSignalSettingsQuery } from "app/pages/sales-intelligence/sub-modules/signals/query/useAccountSignalSettingsQuery";
import { selectSignalsSource } from "app/pages/sales-intelligence/sub-modules/signals/store/selectors";
import { AssetsService } from "app/services/AssetsService";

type SignalsListProps = {
    signals: readonly UISignal<SignalFilters>[];
    selectedSignalType: SignalType;
    onSignalCreate(signalType: SignalType): void;
    onSignalDelete<F extends SignalFilters>(signal: Readonly<UISignal<F>>): void;
    onSignalEdit<F extends SignalFilters>(signal: Readonly<UISignal<F>>): void;
};

const ICON_IMAGE_PATH = AssetsService.assetUrl("/images/intent/hot_locked.svg");

const SignalsList = (props: SignalsListProps) => {
    const { signals, selectedSignalType, onSignalCreate, onSignalDelete, onSignalEdit } = props;
    const selectedTopics: readonly Topic[] = useSelector(selectIntentSignalSelectedTopics);
    const selectedSignalSource = useSelector(selectSignalsSource);
    const isIntentSignal: boolean = selectedSignalType === signalTypeMap.intent;
    const { data: accountSettings } = useAccountSignalSettingsQuery();
    const isUserCustomizationAllowed = accountSettings?.allowUserCustomization ?? true;

    const handleAddSignalClick = () => {
        onSignalCreate(selectedSignalType);
    };

    const isRestApiIntentBlocked =
        selectedSignalSource === SignalSource.RestApi &&
        isIntentSignal &&
        !salesSettingsHelper.isIntentSignalsEnabled();
    const isSignalsWebhookApiIntentBlocked =
        selectedSignalSource === SignalSource.Api &&
        isIntentSignal &&
        !salesSettingsHelper.isSalesApiSignalsIntentEnabled;

    if (isRestApiIntentBlocked || isSignalsWebhookApiIntentBlocked) {
        return (
            <StyledSignalsSection>
                <StyledIntentEmptyTopicStateWrapper>
                    <IntentDataBanner
                        title={translate("si.intent.management.banner.intent-data.title")}
                        description={translate("si.intent.management.banner.intent-data.description")}
                        iconImagePath={ICON_IMAGE_PATH}
                        buttonText={translate("si.common.contact_us")}
                        buttonIconName="sparkles"
                    />
                </StyledIntentEmptyTopicStateWrapper>
            </StyledSignalsSection>
        );
    }

    if (isIntentSignal && selectedTopics.length === 0 && !siNavigator.isAccountSignalsManagementPage) {
        return (
            <StyledSignalsSection>
                <StyledIntentEmptyTopicStateWrapper>
                    <IntentEmptyTopicsBanner />
                </StyledIntentEmptyTopicStateWrapper>
            </StyledSignalsSection>
        );
    }

    return (
        <StyledSignalsSection>
            {signals.length === 0 ? (
                <SignalsEmptyState
                    signalType={selectedSignalType}
                    onAddSignalClick={handleAddSignalClick}
                />
            ) : (
                <StyledSignalsWrapper data-automation="signals-list-wrapper">
                    <StyledScrollableSignalsList>
                        <SignalsListItems
                            signals={signals}
                            selectedSignalType={selectedSignalType}
                            isUserCustomizationAllowed={isUserCustomizationAllowed}
                            onSignalCreate={onSignalCreate}
                            onSignalEdit={onSignalEdit}
                            onSignalDelete={onSignalDelete}
                        />
                    </StyledScrollableSignalsList>
                </StyledSignalsWrapper>
            )}
        </StyledSignalsSection>
    );
};

export default SignalsList;
